import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import {
  buildDonationAmountsVM,
  CardFeeType,
  CheckoutSummaryVM,
  DonationFlowState,
  FeeCalculationStatus,
} from '../../types/donation-flow.models';
import { campaignsAdapter } from '../campaing/reducer';
import { Campaign } from '../../types/campaigns.models';
import {
  selectAllCampaigns,
  selectPopupCampaigns,
} from '../campaing/selectors';
import { CustomerRequest } from '../../types/stripe-models';
import { buildCustomerRequest, buildDonorFormValue } from '../../types/donor-mappers';

export const DONATION_FLOW_FEATURE_KEY = 'donationFlow';
export const selectDonationFlow = createFeatureSelector<DonationFlowState>(
  DONATION_FLOW_FEATURE_KEY
);

export const selectIsLoading = createSelector(
  selectDonationFlow,
  (s) => s.isLoading
);
export const selectStep = createSelector(selectDonationFlow, (s) => s.step);
export const selectDonationType = createSelector(
  selectDonationFlow,
  (s) => s.donationType
);

export const selectDonationMetadata = createSelector(
  selectDonationFlow,
  (s) => s.metadata
);

export const selectFlowMode = createSelector(
  selectDonationFlow,
  (s) => s.flowMode
);
export const selectPriceDraft = createSelector(
  selectDonationFlow,
  (s) => s.priceDraft
);

export const selectCustomer = createSelector(
  selectDonationFlow,
  (s) => s.customer
);
export const selectPrice = createSelector(selectDonationFlow, (s) => s.price);
// export const selectPaymentIntent = createSelector(
//   selectDonationFlow,
//   (s) => s.paymentIntent
// );

export const selectCardFeeType = createSelector(
  selectDonationFlow,
  (state) => state.cardFeeType
);

export const selectFeeType = createSelector(
  selectCardFeeType,
  (feeType) => feeType ?? CardFeeType.Us
);

export const selectFeeReview = createSelector(
  selectDonationFlow,
  (state) => state.feeReview
);

export const selectFeeReviewVm = createSelector(
  selectFeeReview,
  (review) => ({
    visible: !!review?.visible,
    feeType: review?.feeType ?? CardFeeType.Us,
    currency: (review?.currency ?? 'USD').toUpperCase(),
    baseMinor: review?.baseMinor ?? 0,
    oldFeeMinor: review?.oldFeeMinor ?? 0,
    newFeeMinor: review?.newFeeMinor ?? 0,
  })
);

export const selectSetupIntent = createSelector(
  selectDonationFlow,
  (s) => s.setupIntent
);
export const selectPaymentMethodId = createSelector(
  selectDonationFlow,
  (s) => s.paymentMethodId
);
export const selectSubscription = createSelector(
  selectDonationFlow,
  (s) => s.subscription
);
export const selectError = createSelector(selectDonationFlow, (s) => s.error);

// view model selectors
// ==== feesCovered (igual que ya lo tienes) ====
export const selectFeesCovered = createSelector(
  selectDonationFlow,
  (s) => !!s.feesCovered
);

export const selectDeferFirstCharge = createSelector(
  selectDonationFlow,
  (s) => !!s.isFirstChargeDeferred
);

export const selectFirstPaymentDate = createSelector(
  selectDonationFlow,
  (s) => s.firstPaymentDate
);

export const selectDonationAmounts = createSelector(
  selectPriceDraft,
  selectDonationType,
  selectFeesCovered,
  selectFeeType,
  (draft, donationType, feesCovered, feeType) => {
    const baseMinor = draft?.unitAmount ? Number(draft.unitAmount) : 0;
    const currency = draft?.currency ?? 'USD';

    // No silent fallback: when the flow hasn't set a donation type yet we
    // return a zero-VM tagged as null so downstream views (checkout, thanks)
    // can render an empty state instead of pretending it's OneTime.
    if (!donationType) {
      return {
        baseMinor,
        feeMinor: 0,
        totalPaidMinor: baseMinor,
        netReceivedMinor: baseMinor,
        currency,
        donationType: null,
        feesCovered,
      };
    }

    return buildDonationAmountsVM(
      baseMinor,
      currency,
      donationType,
      feesCovered,
      feeType
    );
  }
);

export const selectInvoiceAmounts = createSelector(
  selectDonationAmounts,
  (amounts) => ({
    baseMinor: amounts.baseMinor,
    feeMinor: amounts.feeMinor,
    feesCovered: amounts.feesCovered,
    currency: amounts.currency,
  })
);

export const selectSubscriptionAmounts = createSelector(
  selectDonationAmounts,
  (amounts) => ({
    baseMinor: amounts.baseMinor,
    feeMinor: amounts.feeMinor,
    feesCovered: amounts.feesCovered,
    currency: amounts.currency,
    donationType: amounts.donationType,
  })
);

// select for the view renders
export const selectFeeCalculationStatus = createSelector(
  selectDonationFlow,
  (s): FeeCalculationStatus => s.feeCalculationStatus
);

export const selectCheckoutSummaryVM = createSelector(
  selectDonationAmounts,
  selectFeeCalculationStatus,
  (amounts, feeCalcStatus): CheckoutSummaryVM => ({
    donationAmount: amounts.baseMinor / 100,
    donationFees: feeCalcStatus === 'calculated' ? amounts.feeMinor / 100 : undefined,
    currency: amounts.currency,
    donationType: amounts.donationType,
    feesCovered: amounts.feesCovered,
    hasCalculatedFees: feeCalcStatus === 'calculated',
    isCalculatingFees: feeCalcStatus === 'loading',
  })
);

export const selectRestartCustomerRequest = createSelector(
  selectCustomer,
  selectDonationMetadata,
  (customer, metadata): CustomerRequest | null => {
    if (!customer || !metadata) return null;

    const donorFormValue = buildDonorFormValue(customer, metadata);
    return buildCustomerRequest(donorFormValue);
  }
);

/** Campañas */
const { selectEntities, selectAll } = campaignsAdapter.getSelectors(
  createFeatureSelector<any>('campaigns')
);
export const selectSelectedCampaignId = createSelector(
  createFeatureSelector<any>('campaigns'),
  (s) => s.selectedId as string | null
);
export const selectSelectedCampaign = createSelector(
  selectEntities,
  selectSelectedCampaignId,
  (e, id) => (id ? e[id] ?? null : null)
);
export const selectSelectedCampaignProductId = createSelector(
  selectSelectedCampaign,
  (c) => c?.productId ?? null
);

export type FlowMode = 'single' | 'multiple' | null;

/** Proyector puro */
export const computeFlowMode = (
  campaigns: ReadonlyArray<Campaign>
): FlowMode => {
  if (!campaigns.length) return null;

  let active = 0;
  for (const c of campaigns) {
    if ((c as any).isActive) {
      active++;
      if (active > 1) return 'multiple';
    }
  }
  return active === 1 ? 'single' : null;
};

/** Factory: acepta un selector que **devuelva array** (mutable o readonly) */
export function buildFlowSelectors<
  S,
  R extends ReadonlyArray<Campaign> | Campaign[]
>(campaignsSelector: MemoizedSelector<S, R>) {
  const selectAutoFlowMode = createSelector(
    campaignsSelector,
    (cs): FlowMode => computeFlowMode(cs as ReadonlyArray<Campaign>)
  );

  const selectShowCampaignsColumn = createSelector(
    selectAutoFlowMode,
    (mode) => mode === 'multiple'
  );

  return { selectAutoFlowMode, selectShowCampaignsColumn };
}

export const {
  selectAutoFlowMode: selectAutoFlowModeAll,
  selectShowCampaignsColumn: selectShowCampaignsColumnAll,
} = buildFlowSelectors(selectAllCampaigns);

export const {
  selectAutoFlowMode: selectAutoFlowModePopup,
  selectShowCampaignsColumn: selectShowCampaignsColumnPopup,
} = buildFlowSelectors(selectPopupCampaigns);
